import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Loader2, Play, Crown } from "lucide-react";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const gbp = (v) => (v == null ? "—" : `£${Math.round(v).toLocaleString()}`);
const gbpCompact = (v) => (v >= 1e6 ? `£${(v / 1e6).toFixed(1)}m` : v >= 1e3 ? `£${(v / 1e3).toFixed(0)}k` : `£${Math.round(v)}`);

const SCENARIOS = [
  { value: "cautious", label: "Cautious · 4% return, 6% vol" },
  { value: "balanced", label: "Balanced · 6.5% return, 10% vol" },
  { value: "adventurous", label: "Adventurous · 8.5% return, 14% vol" },
  { value: "custom", label: "Custom assumptions" },
];

const StatCard = ({ label, value, sub, valueClass, testId }) => (
  <div className="widget p-4" data-testid={testId}>
    <div className="overline-label">{label}</div>
    <div className={`font-mono text-xl mt-2 ${valueClass || "text-white"}`}>{value}</div>
    {sub && <div className="font-mono text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function Simulator() {
  const [form, setForm] = useState({
    starting: "10000", monthly: "500", years: 20, scenario: "balanced",
    custom_return_pct: "7", custom_vol_pct: "12", fee_pct: "0.25", inflation_adjust: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (f = form) => {
    setLoading(true);
    try {
      const body = {
        starting: Number(f.starting || 0), monthly: Number(f.monthly || 0), years: f.years,
        scenario: f.scenario, fee_pct: Number(f.fee_pct || 0), inflation_adjust: f.inflation_adjust,
      };
      if (f.scenario === "custom") {
        body.custom_return_pct = Number(f.custom_return_pct || 0);
        body.custom_vol_pct = Number(f.custom_vol_pct || 0);
      }
      const { data } = await api.post("/simulator/run", body);
      setResult(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const a = result?.assumptions;

  return (
    <div className="space-y-6 fade-up">
      <div>
        <div className="overline-label mb-2">Compounding, visualised</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white flex items-center gap-3">
          Wealth Simulator <TrendingUp className="w-8 h-8 text-primary" />
        </h1>
      </div>

      <div className="widget p-6" data-testid="simulator-form">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="overline-label">Starting amount £</Label>
            <Input type="number" min="0" className="rounded-sm bg-background font-mono" data-testid="sim-starting-input"
              value={form.starting} onChange={(e) => setForm({ ...form, starting: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="overline-label">Monthly contribution £</Label>
            <Input type="number" min="0" className="rounded-sm bg-background font-mono" data-testid="sim-monthly-input"
              value={form.monthly} onChange={(e) => setForm({ ...form, monthly: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="overline-label">Scenario</Label>
            <Select value={form.scenario} onValueChange={(v) => setForm({ ...form, scenario: v })}>
              <SelectTrigger className="rounded-sm bg-background h-10" data-testid="sim-scenario-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-sm">
                {SCENARIOS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="overline-label">Annual fee %</Label>
            <Input type="number" min="0" step="0.05" className="rounded-sm bg-background font-mono" data-testid="sim-fee-input"
              value={form.fee_pct} onChange={(e) => setForm({ ...form, fee_pct: e.target.value })} />
          </div>
          {form.scenario === "custom" && (
            <>
              <div className="space-y-1.5">
                <Label className="overline-label">Custom return % / yr</Label>
                <Input type="number" step="0.5" className="rounded-sm bg-background font-mono" data-testid="sim-custom-return-input"
                  value={form.custom_return_pct} onChange={(e) => setForm({ ...form, custom_return_pct: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline-label">Custom volatility %</Label>
                <Input type="number" min="0" step="0.5" className="rounded-sm bg-background font-mono" data-testid="sim-custom-vol-input"
                  value={form.custom_vol_pct} onChange={(e) => setForm({ ...form, custom_vol_pct: e.target.value })} />
              </div>
            </>
          )}
          <div className="space-y-1.5 col-span-2">
            <Label className="overline-label">Time horizon: <span className="text-primary font-mono">{form.years} years</span></Label>
            <Slider value={[form.years]} min={1} max={50} step={1} onValueChange={([v]) => setForm({ ...form, years: v })}
              className="pt-2" data-testid="sim-years-slider" />
          </div>
          <div className="flex items-center gap-3 pt-4">
            <Switch checked={form.inflation_adjust} onCheckedChange={(v) => setForm({ ...form, inflation_adjust: v })}
              data-testid="sim-inflation-switch" />
            <Label className="text-xs text-muted-foreground">Show in today's money (2.5% inflation)</Label>
          </div>
          <div className="flex items-end">
            <Button onClick={() => run()} disabled={loading}
              className="rounded-sm bg-primary hover:bg-primary/85 text-white w-full gap-2" data-testid="sim-run-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run simulation
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sim-results-grid">
            <StatCard label="Projected value" value={gbp(result.final.value)} valueClass="text-gain"
              sub={`deterministic @ ${a.net_return_pct}%/yr net`} testId="sim-final-value-card" />
            <StatCard label="Total contributed" value={gbp(result.final.contributed)} testId="sim-contributed-card" />
            <StatCard label="Investment growth" value={gbp(result.final.growth)} valueClass="text-primary" testId="sim-growth-card" />
          {result && !result.monte_carlo_locked && (
            <StatCard label="Range of outcomes" value={`${gbpCompact(result.final.p10)} – ${gbpCompact(result.final.p90)}`}
              sub={`10th–90th percentile of ${a.simulations} sims · median ${gbpCompact(result.final.p50)}`} testId="sim-range-card" />
          )}
          {result?.monte_carlo_locked && (
            <Link to="/upgrade" className="widget p-4 border-yellow-500/40 block hover:bg-yellow-500/5 transition-colors duration-150"
              data-testid="sim-mc-locked-card">
              <div className="overline-label flex items-center gap-1.5 text-yellow-500">
                <Crown className="w-3.5 h-3.5" /> Range of outcomes
              </div>
              <div className="font-mono text-sm text-white mt-2">Monte Carlo bands are Premium</div>
              <div className="font-mono text-xs text-muted-foreground mt-1">Upgrade to see 10th–90th percentile outcomes →</div>
            </Link>
          )}
          </div>

          <div className="widget p-5" data-testid="sim-chart">
            <div className="overline-label mb-4">
              Growth over {form.years} years {a.inflation_adjusted && "· in today's money"}
            </div>
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart data={result.series} margin={{ left: 10, right: 10 }}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: "#71717a", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  tickFormatter={(y) => `Y${y}`} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={gbpCompact} width={64} />
                <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #27272a", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  labelFormatter={(y) => `Year ${y}`}
                  formatter={(v, name) => [Array.isArray(v) ? `${gbp(v[0])} – ${gbp(v[1])}` : gbp(v), name]} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                {!result.monte_carlo_locked && (
                  <Area dataKey={(d) => [d.p10, d.p90]} name="10–90% band" fill="#2563eb" fillOpacity={0.14} stroke="none" />
                )}
                {!result.monte_carlo_locked && (
                  <Line dataKey="p50" name="Median sim" stroke="#eab308" strokeDasharray="5 4" strokeWidth={1.5} dot={false} />
                )}
                <Line dataKey="value" name="Expected path" stroke="#2563eb" strokeWidth={2.5} dot={false} />
                <Line dataKey="contributed" name="Contributed" stroke="#71717a" strokeWidth={1.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="widget p-5 space-y-2" data-testid="sim-notes">
            <p className="text-xs text-muted-foreground">{result.isa_note}</p>
            <p className="text-xs text-muted-foreground">{result.note}</p>
            <p className="text-xs text-muted-foreground border-t border-border pt-2">{result.disclaimer}</p>
          </div>
        </>
      )}
    </div>
  );
}
